<template>
  <DefaultPanel :is-collapsible="true">
    <template #header>
      <span class="icon">
        <i class="fa-solid fa-box-archive" />
      </span>
      RO-Crate
    </template>
    <template
      v-if="rocrate.source !== 'imported' && !rocrate.isPrimaryRocrate"
      #header-controls
    >
      <!-- Regenerate: DropdownButton with inline confirmation -->
      <DropdownButton
        v-if="rocrate.status === 'ready'"
        size="small"
        variant="warning"
        :outlined="true"
        :icon="['fa-solid', 'fa-rotate']"
        :loading="rocrate.isLaunching"
        label="Regenerate"
        align="end"
        :show-arrow="false"
        content-class="dd-content--confirm"
      >
        <div class="dd-confirm-body">
          <p class="mb-0">
            The existing RO-Crate will be replaced with a newly generated one.
          </p>
          <button
            class="button is-warning is-outlined is-small has-text-weight-bold dd-confirm-btn"
            :disabled="rocrate.isLaunching"
            @click="openGenerateDialog"
          >
            <span class="icon"><i class="fa-solid fa-rotate" /></span>
            <span>Yes, regenerate</span>
          </button>
        </div>
      </DropdownButton>
      <!-- Generate (idle) -->
      <BaseButton
        v-else-if="rocrate.status === 'idle'"
        size="small"
        variant="primary"
        :outlined="true"
        :icon="['fa-solid', 'fa-wand-magic-sparkles']"
        :loading="rocrate.isLaunching"
        @click="openGenerateDialog"
      >
        Generate
      </BaseButton>
    </template>

    <div class="rcs-body">
      <!-- In progress -->
      <div v-if="rocrate.status === 'in_progress'" class="rcs-in-progress">
        <span class="icon"><i class="fa-solid fa-spinner fa-spin" /></span>
        Generating… you will receive an email when the RO-Crate is ready.
      </div>

      <!-- Ready: WV — reuse AttachmentFile for consistent look -->
      <AttachmentFile
        v-else-if="
          rocrate.status === 'ready' &&
          modelName === 'workflowversions' &&
          rocrateAttachments.length
        "
        :attached-file="rocrateAttachments[0]"
        :can-delete="!rocrateAttachments[0].isPrimaryRocrate"
        :is-deleting="isDeletingRocrate"
        :show-timestamp="false"
        @delete="handleRocrateDelete"
      />

      <!-- Ready: WR path -->
      <template v-else-if="rocrate.status === 'ready' && rocrate.path">
        <div class="rcs-path-row">
          <code class="rcs-path">{{ rocrate.path }}</code>
          <BaseButton
            size="small"
            :outlined="true"
            :icon="['fa-regular', 'fa-clipboard']"
            @click="rocrate.copyPath()"
          >
            Copy path
          </BaseButton>
        </div>
        <div v-if="rocrate.generatedAt" class="rcs-meta">
          Generated {{ formatGeneratedAt(rocrate.generatedAt) }}
          <span v-if="rocrate.generatedBy">by {{ rocrate.generatedBy }}</span>
        </div>
      </template>

      <!-- Idle: empty state -->
      <p v-else-if="rocrate.status === 'idle'" class="rcs-empty-text">
        No RO-Crate has been generated for this {{ objectLabel }} yet.
      </p>
    </div>

    <RocrateGenerateDialog
      v-if="dialogOpen"
      :model-name="modelName"
      :custom-path-allowed="rocrate.customPathAllowed"
      :allowed-roots="rocrate.allowedRoots"
      @cancel="dialogOpen = false"
      @confirm="onConfirmGenerate"
    />
  </DefaultPanel>
</template>

<script>
import moment from 'moment'
import { onMounted, reactive, ref, watch } from 'vue'

import { del, get } from '@/api'
import { useRocrateExport } from '@/composables/useRocrateExport'
import {
  allowedEmbedFileTypes,
  matchFileExtensionToIconClasses,
  modelNameSingularize,
  prettySize,
} from '@/utils'

import AttachmentFile from '@/components/AttachmentFile.vue'
import DefaultPanel from '@/components/DefaultPanel.vue'
import RocrateGenerateDialog from '@/components/RocrateGenerateDialog.vue'

const transformAttachment = (a) => {
  let iconClasses = matchFileExtensionToIconClasses(a.filetype)
  iconClasses = iconClasses
    ? [...iconClasses, 'fa-2x']
    : ['fa-regular', 'fa-file', 'fa-2x']
  return {
    created: a.created,
    modified: a.modified,
    prettyDates: {
      created: moment(a.created).format("D MMM 'YY, HH:mm"),
      modified: moment(a.modified).format("D MMM 'YY, HH:mm"),
    },
    createdBy: a.created_by,
    editUrl: a.edit_url,
    directUrl: a.direct_url,
    id: a.id,
    isAutogenerated: a.autogenerated === true,
    isEmbedded: a.embedded === true,
    isEmbeddable: allowedEmbedFileTypes.includes(a.mimetype),
    isExport: a.is_export === true,
    modifiedBy: a.modified_by,
    name: a.name,
    fileType: a.filetype,
    mimeType: a.mimetype,
    preview: a.peek,
    size: prettySize(a.size),
    thumbnailUrl: a.thumbnail_url,
    tsrUrl: a.tsr_url,
    wopiTokenUrl: a.wopi_token_url,
    iconClasses,
    isWithinTimeStampGenerationDelta:
      moment().diff(moment(a.modified), 'minutes') < 5,
    hasBeenModifiedAfterCreation:
      moment(a.modified).diff(moment(a.created), 'second') > 0,
    isPrimaryRocrate: a.primary_rocrate ?? false,
  }
}

export default {
  name: 'RocrateSection',
  components: { AttachmentFile, DefaultPanel, RocrateGenerateDialog },
  props: {
    appName: { type: String, required: true },
    modelName: { type: String, required: true }, // 'workflowversions' | 'workflowruns'
    recordId: { type: [String, Object], required: true },
  },
  setup(props) {
    const dialogOpen = ref(false)
    const rocrateAttachments = ref([])
    const isDeletingRocrate = ref(false)

    const rocrate = reactive(
      useRocrateExport({
        appName: props.appName,
        modelName: props.modelName,
        recordId: props.recordId,
      }),
    )

    const objectLabel =
      props.modelName === 'workflowversions'
        ? 'workflow version'
        : 'workflow run'

    const formatGeneratedAt = (iso) =>
      iso ? moment(iso).format('DD/MM/YY - HH:mm:ss') : ''

    const resolvedRecordId = () =>
      typeof props.recordId === 'object'
        ? props.recordId?.value
        : props.recordId

    const loadRocrateAttachments = async () => {
      const recordId = resolvedRecordId()
      if (!recordId) return
      try {
        const singularModel = modelNameSingularize(props.modelName)
        const url = `attachments/?app=${props.appName}&model=${singularModel}&object_id=${recordId}&is_rocrate=true&page_size=max`
        const response = await get(url)
        rocrateAttachments.value = Array.isArray(response?.data)
          ? response.data.map(transformAttachment)
          : []
      } catch {
        rocrateAttachments.value = []
      }
    }

    const openGenerateDialog = () => {
      if (props.modelName === 'workflowruns') {
        dialogOpen.value = true
      } else {
        rocrate.requestGeneration()
      }
    }

    const onConfirmGenerate = ({ root, subpath }) => {
      dialogOpen.value = false
      rocrate.requestGeneration({ root, subpath })
    }

    const handleRocrateDelete = async () => {
      const attachment = rocrateAttachments.value[0]
      if (!attachment) return
      isDeletingRocrate.value = true
      try {
        await del(`attachments/${attachment.id}/`)
        rocrateAttachments.value = []
        await rocrate.fetchStatus()
      } finally {
        isDeletingRocrate.value = false
      }
    }

    onMounted(async () => {
      await rocrate.fetchStatus()
      if (rocrate.status === 'ready') {
        await loadRocrateAttachments()
      }
    })

    watch(
      () => rocrate.status,
      (newStatus) => {
        if (newStatus === 'ready') loadRocrateAttachments()
      },
    )

    watch(
      () =>
        typeof props.recordId === 'object'
          ? props.recordId?.value
          : props.recordId,
      (id) => id && rocrate.fetchStatus(),
    )

    return {
      rocrate,
      dialogOpen,
      rocrateAttachments,
      isDeletingRocrate,
      objectLabel,
      openGenerateDialog,
      onConfirmGenerate,
      handleRocrateDelete,
      formatGeneratedAt,
    }
  },
}
</script>

<style lang="scss" scoped>
.rcs-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rcs-notice {
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.rcs-in-progress {
  color: var(--text-light);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.rcs-empty-text {
  color: var(--text-light);
  font-size: 0.875rem;
  margin: 0;
}

.rcs-path-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.rcs-path {
  display: block;
  padding: 0.35rem 0.6rem;
  background: var(--bg-subtle);
  border-radius: 4px;
  word-break: break-all;
  font-size: 0.85rem;
  flex: 1;
  min-width: 0;
}

.rcs-meta {
  color: var(--text-light);
  font-size: 0.8rem;
}
</style>
